
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from "@/components/ui/card";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { Link } from "react-router-dom";
import { ResizableSwappableCard } from "@/components/admin/dashboard/ResizableSwappableCard";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

interface UpcomingExpirationsTableProps {
  upcomingExpirations: any[];
  rearrangingEnabled: boolean;
  onRemove: (id: string) => void;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export function UpcomingExpirationsTable({
  upcomingExpirations,
  rearrangingEnabled,
  onRemove,
  size,
  onResize
}: UpcomingExpirationsTableProps) {
  const { t } = useEnhancedTranslation();
  const today = new Date();
  
  const getRowColor = (expiryDate: string) => {
    const daysLeft = differenceInDays(new Date(expiryDate), today);
    if (daysLeft <= 7) return "text-red-500";
    if (daysLeft <= 30) return "text-amber-500";
    return "";
  };

  return (
    <ResizableSwappableCard
      id="upcoming-expirations"
      size={size}
      showHandle={rearrangingEnabled}
      showResizeControl={rearrangingEnabled}
      onRemove={rearrangingEnabled ? onRemove : undefined}
      onResize={rearrangingEnabled && onResize ? (newSize) => onResize("upcoming-expirations", newSize) : undefined}
    >
      <CardHeader>
        <CardTitle>{t('dashboard.widgets.upcomingExpirations')}</CardTitle>
      </CardHeader>
      <CardContent>
        {upcomingExpirations.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('dashboard.widgets.supplier')}</TableHead>
                <TableHead>{t('dashboard.widgets.expires')}</TableHead>
                <TableHead>{t('dashboard.widgets.daysLeft')}</TableHead>
                <TableHead className="text-right">{t('dashboard.widgets.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingExpirations.map((contract) => {
                const expiryDate = new Date(contract.expiry_date);
                const daysLeft = differenceInDays(expiryDate, today);
                
                return (
                  <TableRow key={contract.id} className={getRowColor(contract.expiry_date)}>
                    <TableCell className="font-medium">{contract.supplier_name}</TableCell>
                    <TableCell>{format(expiryDate, 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      {t('dashboard.widgets.noDaysLeft', { count: daysLeft })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/contracts/${contract.id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground">
            {t('dashboard.widgets.noUpcomingExpirations')}
          </div>
        )}
      </CardContent>
    </ResizableSwappableCard>
  );
}
